import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { type CarbonIconType as LucideIcon } from '@carbon/icons-react'

interface StatBlockProps {
  number: string
  label: string
  description?: string
  icon?: LucideIcon
  color?: string
  className?: string
}

export function StatBlock({
  number,
  label,
  description,
  icon: Icon,
  color = 'from-[#37ecb0] to-[#2abf8f]',
  className,
}: StatBlockProps) {
  return (
    <div className={cn('border border-border rounded-lg p-6', className)}>
      <div>
        {Icon && (
          <div className="mb-4">
            <Icon className="w-8 h-8 text-emerald-600 dark:text-primary-active" />
          </div>
        )}
        <div className="text-4xl font-bold text-foreground mb-2">
          <AnimatedNumber value={number} />
        </div>
        <div className="text-base font-semibold text-foreground mb-1">
          {label}
        </div>
        {description && (
          <div className="text-sm text-muted-foreground mt-2">
            {description}
          </div>
        )}
      </div>
    </div>
  )
}

interface BigNumberProps {
  value: string
  label: string
  subtext?: string
  inline?: boolean
}

export function BigNumber({ value, label, subtext, inline }: BigNumberProps) {
  if (inline) {
    return (
      <span className="inline-flex items-baseline gap-2">
        <span className="text-3xl font-bold text-emerald-600 dark:text-primary-active">
          <AnimatedNumber value={value} />
        </span>
        <span className="text-lg font-semibold text-foreground">{label}</span>
      </span>
    )
  }

  return (
    <div className="text-center py-8 border border-border rounded-lg my-8">
      <div className="text-5xl md:text-6xl font-bold text-emerald-600 dark:text-primary-active mb-3">
        <AnimatedNumber value={value} />
      </div>
      <div className="text-xl font-semibold text-foreground mb-2">{label}</div>
      {subtext && (
        <div className="text-sm text-muted-foreground">{subtext}</div>
      )}
    </div>
  )
}

interface StatsGridProps {
  children: React.ReactNode
  columns?: 2 | 3 | 4
}

export function StatsGrid({ children, columns = 3 }: StatsGridProps) {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  return (
    <div className={cn('grid gap-6 my-8', gridCols[columns])}>{children}</div>
  )
}
